import React, { Fragment, useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { bool, string, array, oneOfType, number, object } from 'prop-types';
import { map, get, isEmpty, head, filter, inRange, toNumber, size } from 'lodash';
import moment from 'moment';
import { Text, FlexContainer, Row, Divider, Column, IconContainer, Icon, Button, HasPermissions, Truncate, AppIcon } from '../../common';
import { Switch, Modal, Drawer, Tooltip } from 'antd';
import { withApollo } from 'react-apollo';
import SWITCH_WINDOW from '../../graphql/automaticAssignment';
import { calculateBillingDeadline, getFilterSlaType } from '../../../utils/misc';
import { getWindowLabel } from '../../../utils/dynamicWindow';

const VerticalSeparator = styled.div`
    border-left: 1px solid #eaeaea;
    width: 1px;
    display: inline-block;
    margin-left: 15px;
    margin-right: 15px;
`;

const CounterButton = styled.div`
    text-align: center;
    border-radius: 11.5px;

    ${({ hover }) => hover && css`&:hover {
        background-color: ${({ backgroundColorOnHover = '#969696' }) => backgroundColorOnHover};
        span {
            color: #fff;
        }
        cursor: pointer;
    }`};
`;

const StatusField = ({ label, value, salesChannel, isTotal, backgroundColorOnHover, dateRange, selectedSalesChannels, color, filterSelectedSla, nameCarrier, withPersonalShopper, atrasado }) => {
    const leftColumWidth = '44px';

    const getStatusFilter = () => {
        // Atrasadas: statuses del counter atrasado (incluye 22). No cambia el click de Pendientes.
        if (atrasado) return '&status=1-2-7-20-22';
        switch (label) {
            case 'Pendientes':
                return '&status=1-2-7-20';
            case 'En picking':
                return '&status=3-6-30-32-4-8-9';
            case 'Pickeados':
                return '&status=5-50-51-53-54-55-56-57-58-59-60';
            case 'Anulados':
                return '&status=40-41-34';
            default:
                return '';
        };
    };

    const getSearch = () => {
        const base = `nameCarrier=${nameCarrier}&selectedSla=${encodeURIComponent([].concat(filterSelectedSla || []).join(','))}${getStatusFilter()}&salesChannel=${(salesChannel || selectedSalesChannels) || ''}&range=${dateRange || ''}&fromRoomManagement=true`;
        return atrasado ? `${base}&atrasado=true` : base;
    };

    // Igual que Pendientes+PS / atrasadas: el color del label y del chip vive acá, no se pasa color=#fff desde afuera.
    const getLabelColor = () => {
        if (label === 'Pendientes' && withPersonalShopper > 0) {
            return 'rgb(198, 80, 6)';
        }
        return color;
    };

    const getButtonBackground = () => {
        if (atrasado) return '#CC444B';
        if (label === 'Pendientes' && withPersonalShopper > 0) return 'rgb(255 229 201)';
        return '#f4f4f4';
    };

    const getValueColor = () => {
        if (atrasado) return '#FFFFFF';
        return getLabelColor();
    };

    const getHoverBackground = () => {
        if (atrasado) return '#a8363b';
        return backgroundColorOnHover;
    };

    return (
        <Row padding='3px 0px' align='center'>
            <div style={{ width: `calc(100% - ${leftColumWidth})` }}>
                <Text text={label} strong={isTotal || (label === 'Pendientes')} color={getLabelColor()} />
            </div>
            {toNumber(value) > 0
                ? <Link target='_blank'
                    to={{
                        pathname: '/orders',
                        search: getSearch()
                    }}>
                    <CounterButton
                        hover
                        style={{ width: leftColumWidth, backgroundColor: getButtonBackground() }}
                        backgroundColorOnHover={getHoverBackground()}
                    >
                        {isTotal
                            ? <Text text={value} strong />
                            : <Text text={value} style={{ width: leftColumWidth, textAlign: 'center' }} color={getValueColor()} strong={label === 'Pendientes'} />
                        }
                    </CounterButton>
                </Link>
                : <CounterButton style={{ width: leftColumWidth, backgroundColor: atrasado ? getButtonBackground() : undefined }}>
                    <Text text={value} style={{ width: leftColumWidth, textAlign: 'center' }} color={getValueColor()} strong={label === 'Pendientes'} />
                </CounterButton>
            }
        </Row>
    );
};

StatusField.propTypes = {
    label: string,
    value: string,
    isTotal: bool,
    backgroundColorOnHover: string,
    filterSelectedSla: array,
    salesChannel: oneOfType([string, () => undefined]),
    dateRange: array,
    selectedSalesChannels: array,
    color: string,
    nameCarrier: string,
    withPersonalShopper: number,
    atrasado: bool
};

StatusField.defaultProps = {
    label: '',
    value: 0,
    isTotal: false,
    atrasado: false
};

const SlaCard = ({
    selectedSalesChannels,
    assignmentKind,
    client,
    maxHrsLimitWindow,
    card,
    isMobile,
    hoursAssignDispatch,
    userId,
    windowLogs,
    isDeliveryTomorrow,
    isEarlyDelivery,
    isDynamicWindows
}) => {
    const slaData = get(card, 'data');
    const filterSelectedSla = getFilterSlaType(get(card, 'info.selectedSla'));
    const selectedSla = get(card, 'info.selectedSla');
    const salesChannel = get(card, 'info.salesChannel');
    const dateRange = [get(card, 'info.init'), get(card, 'info.end')];
    const windowInit = get(card, 'info.init');
    const windowEnd = get(card, 'info.end');
    const pending = get(head(filter(slaData, { status: 'pendiente' })), 'count');
    const cancelled = get(head(filter(slaData, { status: 'anulado' })), 'count');
    const inPicking = get(head(filter(slaData, { status: 'inPicking' })), 'count');
    const picked = get(head(filter(slaData, { status: 'facturado' })), 'count');
    const withPersonalShopper = get(head(filter(slaData, { status: 'personalShopper' })), 'count');
    const overdue = get(head(filter(slaData, { status: 'atrasado' })), 'count'); // Jumbo 60: ordenes atrasadas
    const statusAutomaticAssign = get(card, 'info.automaticAssign');
    const isDispatch = selectedSla === 'Despacho';
    const totalOrders = pending + cancelled + inPicking + picked;
    const allPicked = (totalOrders - cancelled) === picked;
    const cutOffTime = get(card, 'info.cutOffTime');
    const maximunCapacity = get(card, 'info.maximunCapacity');
    const consumedCapacity = get(card, 'info.consumedCapacity');
    const category = get(card, 'info.category');
    const time = get(card, 'info.time');
    const nameCarrier = get(card, 'info.nameCarrier');

    // Sala regular (DynamicWindow 0): hora de facturación. Sala dinámica (1): órdenes atrasadas.
    const showBillingDeadline = !isDynamicWindows;
    const showOverdueOrders = !!isDynamicWindows;

    const getDate = () => (
        calculateBillingDeadline(category, time, windowInit, windowEnd).format('YYYY-MM-DD HH:mm:ss')
    );

    const [autoAssigment, setAutoAssigment] = useState(statusAutomaticAssign);
    const [visibleModal, setVisibleModal] = useState(false);
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [loading, setLoading] = useState(false);

    const items = [
        { label: 'Pendientes', count: `${pending}` },
        { label: 'En picking', count: `${inPicking}` },
        { label: 'Pickeados', count: `${picked}` },
        { label: 'Anulados', count: `${cancelled}` }
    ];
    const totals = [
        { label: 'Total', count: `${pending + inPicking + picked + cancelled}` }
    ];

    const dateNow = moment().format('YYYY-MM-DD HH:mm:ss');
    const timeLeftTemp = showBillingDeadline ? moment(getDate()).diff(moment(dateNow), 'seconds') : 0;
    const duration = showBillingDeadline ? moment.duration(moment(getDate()).diff(moment(dateNow))) : null;
    const timeLimitTemp = showBillingDeadline && inRange(timeLeftTemp, 60, 1860);
    let timeOutTemp = true;

    if (showBillingDeadline && !(get(head(filter(slaData, { status: 'fueraDePlazo' })), 'count') > 0)) {
        timeOutTemp = dateNow >= getDate();
    }

    const getColorBrandCard = () => {
        if (timeOutTemp && !allPicked) return '#CC444B';
        if (timeLimitTemp) return '#EB963C';
        return '#e3e3e3';
    };

    const getColorTextTemp = () => {
        if ((timeOutTemp && !allPicked) || timeLimitTemp) return '#FFFFFF';
        return '#000000';
    };

    const formatTimeUnit = (unit) =>
        unit < 10 ? `0${unit}` : unit;

    const handleTemporalizator = () => {
        let temp = {};
        if (showBillingDeadline && timeLeftTemp > 0 && duration) {
            temp = {
                hours: formatTimeUnit(duration.days() >= 1 ? duration.hours() + (24 * duration.days()) : duration.hours()),
                minutes: formatTimeUnit(duration.minutes()),
                seconds: formatTimeUnit(duration.seconds())
            };
        }
        return temp;
    };

    const [temporalizator, setTemporalizator] = useState(handleTemporalizator);

    useEffect(() => {
        if (!showBillingDeadline) return undefined;
        const timeoutId = setTimeout(() => {
            setTemporalizator(handleTemporalizator());
        }, 1000);
        return () => clearTimeout(timeoutId);
    });

    const tempText = `${temporalizator.hours}:${temporalizator.minutes}:${temporalizator.seconds}`;
    const getTempText = () => (!isEmpty(temporalizator) ? tempText : '00:00:00');

    const isOverTimeLimit = (maxHrs) => {
        const hoursLeft = moment(windowInit).diff(moment(), 'hours');
        return hoursLeft > maxHrs;
    };

    const switchWindow = async () => {
        setLoading(true);
        const { data } = await client.mutate({
            mutation: SWITCH_WINDOW,
            variables: {
                input: {
                    turnOn: !autoAssigment ? 'true' : 'false',
                    salesChannel: toNumber(salesChannel),
                    selectedSla,
                    windowStart: windowInit,
                    windowEnd: windowEnd,
                    courierName: nameCarrier,
                    userId
                }
            }
        });
        const { switchWindow: { internalCode } } = data;
        internalCode === '200' && setAutoAssigment(!autoAssigment);
        onClose();
        setLoading(false);
    };

    const onClose = () => {
        setVisibleModal(false);
    };

    const ModalFooter = () => {
        return (
            <FlexContainer justify='flex-end' align='center' style={{ width: '100%' }}>
                <Button
                    link
                    text={'btn.action.cancel'}
                    style={{ width: '100px', margin: 0 }}
                    onClick={() => onClose()}
                />
                <Button
                    loading={loading}
                    text={'btn.action.confirm'}
                    style={{ width: '100px', margin: 0 }}
                    onClick={() => switchWindow()()}
                />
            </FlexContainer>
        );
    };

    const modalWarning = () => {
        return (
            <Modal
                visible={visibleModal}
                footer={<ModalFooter />}
                closable={false}
                width={400}
                destroyOnClose
                onCancel={() => onClose()}
            >
                <Column center>
                    <IconContainer color='#f38630' margin='0 0 40px 0'>
                        <Icon name='caution' size={43} color='#ffffff' />
                    </IconContainer>
                    <Text text={`La hora de entrega esta muy lejana`} size={14} centered />
                    <Text text={`Desea confirmar?`} size={14} centered />
                </Column>
            </Modal>
        );
    };

    const activeRestriction = () => {
        return isDispatch && moment(windowInit).diff(moment(), 'minutes') >= hoursAssignDispatch;
    };

    const renderMaximumQuantity = () => {
        const section = (label, value) => (<Row justify='center' style={{ gap: 5 }}>
            <Text text={value || '0'} strong />
            <Text text={label} />
        </Row>);

        return (<>
            <Divider style={{ marginTop: 5 }} />
            <Row justify='center'>
                <Text text={'Cantidad máxima'} strong />
            </Row>
            <Row mT={10}>
                {section('Ingresadas', consumedCapacity)}
                <VerticalSeparator />
                {section('Capacidad', maximunCapacity)}
            </Row>
        </>);
    };

    const logsFound = filter(windowLogs, ({ windowStart: init, windowEnd: end, courierName, selectedSla: typeDelivery }) => {
        return windowInit === init && windowEnd === end && nameCarrier === courierName && selectedSla === typeDelivery;
    })

    const toggleDrawer = () => setDrawerOpened(!drawerOpened);

    return (
        <FlexContainer
            direction='column'
            style={{
                flex: '1 0 250px',
                backgroundColor: 'white',
                minWidth: isMobile ? '85%' : '18%',
                maxWidth: 360,
                borderRadius: 4,
                boxShadow: 'rgb(0 0 0 / 20%) 3px 0px 0px -4px, rgb(0 0 0 / 6%) 0px 1px 0px 0px, rgb(0 0 0 / 6%) 1px 0px 0px 0px'
            }}
        >
            {showBillingDeadline && (
                <Row style={{ backgroundColor: `${getColorBrandCard()}`, borderTopLeftRadius: '4px', borderTopRightRadius: '4px', padding: '0px 15px' }} justify='space-between'>
                    <Text text={'Límite de Facturación'} color={getColorTextTemp()} />
                    <Text text={getTempText()} color={getColorTextTemp()} />
                </Row>
            )}
            <div style={{ padding: '15px' }}>
                {!isEmpty(nameCarrier) && <>
                    <Row padding='0px 0px 3px 0px' justify='flex-start' align='center' style={{ position: 'relative' }} gap={10} >
                        {isEarlyDelivery && <Tooltip title='Ventana anticipada'>
                            <AppIcon name='ic-location' size={22} />
                        </Tooltip>}
                        <Truncate width={200} style={{ textAlign: 'left' }}>
                            <Link target='_blank'
                                to={{
                                    pathname: '/orders',
                                    search: `nameCarrier=${nameCarrier}&selectedSla=${encodeURIComponent([].concat(filterSelectedSla || []).join(','))}&salesChannel=${(salesChannel || selectedSalesChannels) || ''}&range=${dateRange || ''}&fromRoomManagement=true`
                                }}
                            >
                                <Text text={nameCarrier} cursor='pointer' underlineOnHover strong />
                            </Link>
                        </Truncate>
                        <HasPermissions to={['room-management-page:automatic-assignment-logs']} >
                            {(size(logsFound) > 0) && <Tooltip placement='top' title='Logs de ventana'>
                                <div style={{ position: 'absolute', right: 0 }}>
                                    <Icon
                                        name='logs'
                                        color='#1fa02e'
                                        size={22}
                                        onClick={toggleDrawer}
                                        pointer
                                    />
                                </div>
                            </Tooltip>}
                        </HasPermissions>
                    </Row>
                    <Divider style={{ marginTop: 5 }} />
                </>}

                {/* Despacho + Asig. Auto — mockup Jumbo 60: la fecha ("Despacho") queda alineada con el switch */}
                <Row justify='space-between' align='center'>
                    <Row gap={4} align='center'>
                        <Text text='Despacho:' strong />
                        <Text text={moment(windowInit).format('DD/MM/YYYY')} />
                    </Row>
                    {assignmentKind && <Row justify='flex-end' align='center'>
                        <HasPermissions to={['room-management-page:automatic-assignment']} >
                            <Text text='Asig. Auto' size={10} color={'b8bcbf'} style={{ margin: 'auto 5px' }} />
                            <Switch
                                disabled={activeRestriction()}
                                loading={loading}
                                checked={autoAssigment}
                                onChange={() => {
                                    isOverTimeLimit(maxHrsLimitWindow)
                                        ? setVisibleModal(true)
                                        : switchWindow();
                                }}
                            />
                        </HasPermissions>
                    </Row>
                    }
                </Row>
                {/* Ventana (preparación/entrega) destacada con fondo — mockup Jumbo 60. Label condicional al flag de sala */}
                <div style={{ backgroundColor: '#f4f4f4', borderRadius: 4, padding: '8px 10px', margin: '8px 0' }}>
                    <Text text={`${getWindowLabel(isDynamicWindows)}:`} />
                    <Row gap={5} align='center'>
                        <Text text={`${moment(windowInit).format('HH:mm')} - ${moment(windowEnd).format('HH:mm')}`} strong />
                        {isDeliveryTomorrow && isEarlyDelivery && <Tooltip title='Ventana anticipada (mañana)'>
                            <AppIcon name='ic-tomorrow' size={22} />
                        </Tooltip>}
                    </Row>
                </div>
                <Divider style={{ marginTop: 5 }} />
                {/* Cierre de oferta — sala dinámica: fin de ShippingETA; regular: cutOffTime (= inicio de ShippingETA) */}
                <Row padding='3px 0px' align='center'>
                    <div style={{ width: `calc(100% - 44px)` }}>
                        <Text text={'Cierre de oferta'} strong />
                    </div>
                    <Text text={isDynamicWindows ? moment(windowEnd).format('HH:mm') : cutOffTime} style={{ width: 44, textAlign: 'center' }} strong />
                </Row>
                {showOverdueOrders && (
                    <>
                        <Divider style={{ marginTop: 5 }} />
                        {/* Jumbo 60: órdenes atrasadas solo en salas dinámicas (DynamicWindow 1) */}
                        <StatusField
                            nameCarrier={nameCarrier}
                            label='Órdenes atrasadas en inicio de preparación'
                            value={`${overdue || 0}`}
                            filterSelectedSla={filterSelectedSla}
                            salesChannel={salesChannel}
                            dateRange={dateRange}
                            selectedSalesChannels={selectedSalesChannels}
                            atrasado
                        />
                    </>
                )}
                <Divider style={{ marginTop: 5 }} />
                {/* Card body */}
                {map(items, ({ label, count }, index) => (
                    <Fragment key={index}>
                        <StatusField
                            nameCarrier={nameCarrier}
                            label={label}
                            value={count}
                            selectedSla={selectedSla}
                            filterSelectedSla={filterSelectedSla}
                            salesChannel={salesChannel}
                            dateRange={dateRange}
                            selectedSalesChannels={selectedSalesChannels}
                            withPersonalShopper={withPersonalShopper}
                        />
                    </Fragment>
                ))}
                <Divider style={{ marginTop: 5 }} />
                {/* Card totals */}
                {map(totals, ({ label, count }, index) => <StatusField
                    nameCarrier={nameCarrier}
                    key={index}
                    label={label}
                    isTotal
                    value={count}
                    selectedSla={selectedSla}
                    filterSelectedSla={filterSelectedSla}
                    salesChannel={salesChannel}
                    dateRange={dateRange}
                    selectedSalesChannels={selectedSalesChannels}
                />)}
                {modalWarning()}
                {renderMaximumQuantity()}
            </div>
            <Drawer
                title={<Text text={<div>
                    <strong>Logs de ventana </strong> ({moment(windowInit).format('HH:mm')} - {moment(windowEnd).format('HH:mm')})
                </div>} size={15} />}
                closable
                visible={drawerOpened}
                onClose={toggleDrawer}
                width='50%'
            >
                {map(logsFound, ({ userId, switchType, creationAt }) => {
                    return (
                        <Row padding='3px 0px' align='center'>
                            <Text text={switchType === "true" ? 'Apertura' : 'Cierre'} size={12} style={{ width: 60 }} />
                            <Text text={creationAt} size={12} style={{ width: 130 }} />
                            <Text text={userId} strong size={12} />
                        </Row>
                    )
                })}
            </Drawer>
        </FlexContainer>
    );
};

SlaCard.propTypes = {
    selectedSalesChannels: array,
    assignmentKind: bool,
    client: object,
    maxHrsLimitWindow: number,
    card: object,
    isMobile: bool,
    hoursAssignDispatch: number,
    userId: string,
    isDeliveryTomorrow: bool,
    isEarlyDelivery: bool,
    isDynamicWindows: bool
};

export default withApollo(SlaCard);